import type { Dispatch, JSX, SetStateAction } from 'react'
import { Fragment, createContext, useState } from 'react'
import { Map, fromJS } from 'immutable'

import { Column } from './Column'
import { ElementInsertionMenu } from './ElementInsertionMenu'
import { blockSupportsHeadline, isHeadlineEmpty, isHeadlineHidden } from '../Workspace.helpers'
import { emptyHeadline } from '../../../editorSidebarBlockTemplates'
import { getElements } from './MultiColumnBlock.helpers'
import Element from '../Element'

export const contactAndMapsPlugins = ['epages.contactform', 'epages.gmaps', 'nested-headline-block']

export const ColumnContext = createContext<[number, Dispatch<SetStateAction<number>>]>([-1, () => {}])

export type Props = Readonly<{
  config: UnknownRecord
  data: ImmutableMap
  editorView: boolean
  editorMode: 'view' | 'edit'
  requestModeChange: (editorMode: 'view' | 'edit') => void
  onDataChange: (data: ImmutableMap) => void
}>

export const MultiColumnBlock = makeMultiColumnBlock(false)
export const NestedBlockWithHeadline = makeMultiColumnBlock(true)

function makeMultiColumnBlock(isNested: boolean) {
  function MultiColumnBlock({ config, editorView, editorMode, requestModeChange, data, onDataChange }: Props) {
    const columnState = useState(-1)
    const [addingColumnAtIndex, setAddingColumnAtIndex] = columnState

    let columns: ImmutableList = data.get('columns')

    const isMultiColumn = columns.size > 1
    const isAddingColumn = addingColumnAtIndex > -1
    if (isAddingColumn) columns = columns.insert(addingColumnAtIndex, Map({ _id: 'insert' }))

    const needsNoHeadline = !blockSupportsHeadline(data)

    let renderedHeadline: JSX.Element | null = (
      <Element
        config={config}
        editorView={editorView}
        isMultiColumn={isMultiColumn}
        element={data.get('headline') || fromJS(emptyHeadline)}
        onElementChange={(element: ImmutableMap) => onDataChange(data.set('headline', element))}
      />
    )

    const context =
      isAddingColumn &&
      columns
        .find((column) => contactAndMapsPlugins.includes(column?.get('type')))
        ?.get('type')
        .replace('epages.', '')

    let renderedColumns: JSX.Element | JSX.Element[] = columns
      .map((column: ImmutableMap, index: number) => (
        // fall back to array index for nested contact form blocks (which cannot be moved)
        // and for old data columns without existing _id when rendering in the storefront
        // (see also: Workspace component)
        <Fragment key={column.get('_id') || index}>
          {index === addingColumnAtIndex ? (
            <div className={isNested ? undefined : 'dali-grid-column'}>
              <ElementInsertionMenu
                context={context || 'default'}
                onNewElement={(element) => {
                  onDataChange(data.update('columns', (columns: ImmutableList) => columns.insert(index, element)))
                  setAddingColumnAtIndex(-1)
                }}
                onCancel={() => setAddingColumnAtIndex(-1)}
              />
            </div>
          ) : (
            <Column
              column={column}
              config={config}
              data={data}
              index={isAddingColumn && index > addingColumnAtIndex ? index - 1 : index}
              editorView={editorView}
              parentEditorMode={editorMode}
              parentRequestModeChange={requestModeChange}
              onDataChange={onDataChange}
              isNested={isNested}
            />
          )}
        </Fragment>
      ))
      .toArray()

    if (!isNested) {
      renderedHeadline = <div className="dali-grid-headline">{renderedHeadline}</div>
      renderedColumns = <div className="dali-grid-row">{renderedColumns}</div>
    }

    if ((editorView && needsNoHeadline) || (!editorView && isHeadlineEmpty(data))) renderedHeadline = null

    if (!isNested && isHeadlineHidden(data)) renderedHeadline = null

    return (
      <ColumnContext.Provider value={columnState}>
        {renderedHeadline}
        {renderedColumns}
      </ColumnContext.Provider>
    )
  }

  MultiColumnBlock.getElements = getElements
  MultiColumnBlock.displayName = isNested ? 'NestedBlockWithHeadline' : 'MultiColumnBlock'

  return MultiColumnBlock
}
